import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import {
  ExternalIntegration,
  ExternalIntegrationDataSource,
} from 'dashboard/models/external-integration';
import {
  ExternalIntegrationCall,
  ExternalIntegrationCallLoadingState,
  ExternalIntegrationCallStatus,
} from 'dashboard/models/external-integration-calls';
import {
  Importer,
  ImporterArchiveAfterCompletion,
  ImporterRunType,
  ImporterStatus,
  MANUAL_SCHEDULE_STATE,
  QUERYABLE_IMPORTERS,
} from 'dashboard/models/importer';
import { ImporterRun, ImporterRunStatus } from 'dashboard/models/importer-run';
import { ProductFeatureName } from 'dashboard/models/organization';
import { saveCurrentPage, savePageSize } from 'dashboard/redux/actions/importer-runs';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { clearExternalIntegrationCall } from 'dashboard/redux/slices/external-integration-call';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import { runQueryAndPoll } from 'dashboard/redux/thunk-actions/external-integration-call';
import { loadExternalIntegrations } from 'dashboard/redux/thunk-actions/external-integrations';
import { cancelImporterRun } from 'dashboard/redux/thunk-actions/importer-run';
import {
  createImporterRun,
  loadImporterRunsList,
} from 'dashboard/redux/thunk-actions/importer-runs';
import {
  activateImporter,
  archiveImporter,
  deactivateImporter,
  loadImporter,
  updateImporter,
  updateImporterName,
} from 'dashboard/redux/thunk-actions/importers';
import DashboardImporterView from 'dashboard/views/DashboardImporterView';
import { EditImporterData } from 'dashboard/views/DashboardImporterView/types';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { ExternalIntegrationDataSource as ApiModelExternalIntegrationDataSource } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type DashboardImporterRouteProps = PropsFromRedux;

type Params = { importerId: string };

const DashboardImporterRoute = (props: DashboardImporterRouteProps) => {
  const { currentPage, pageSize, paginationParams } = props;
  const params = useParams<Params>() as Params; // Will always be defined
  const externalIntegrationCallId = useRef<string | null>();
  const { handlePageLinkClick, handlePageSizeClick } = useTable({
    pageSize: pageSize,
    currentPage: currentPage,
    setCurrentPage: props.saveCurrentPage,
    setPageSize: props.savePageSize,
  });

  const handleQueryCancel = () => {
    if (externalIntegrationCallId.current) {
      externalIntegrationCallId.current = null;
    }
    updateQueryLoadingState(ExternalIntegrationCallStatus.Cancelled);
  };

  const { loading: isLoadingAccountTypes } = useApiLoader({
    loader: () => {
      return props.loadAccountTypes();
    },
    deps: [props.currentEnvironment],
    onError: () => {
      props.showMessage('Failed to load importer, please try again', MessageType.Failure);
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      );
    },
  });

  const {
    isLoading: isLoadingTransactionTypes,
    data: { data: transactionTypes = [] } = {},
    error: transactionTypesError,
  } = useListTransactionTypesQuery({ filter: {} });

  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({
      message: 'Failed to load importer, please try again',
      level: MessageType.Failure,
    }),
  });

  const onError = (error: Error) => {
    updateQueryLoadingState(null);
    externalIntegrationCallId.current = null;
    if (error instanceof ApiResponseError && isPermissionsError(error)) {
      props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
    } else {
      props.showMessage(
        'Failed to query external data sources, please try again',
        MessageType.Failure,
      );
    }
  };

  const onNetworkError = () => {
    updateQueryLoadingState(null);
    externalIntegrationCallId.current = null;
    props.showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);
  };

  const [queryLoadingState, unwrappedSetQueryLoadingState] =
    useState<ExternalIntegrationCallLoadingState>({
      status: null,
    });

  const onQueryError = () => {
    updateQueryLoadingState(ExternalIntegrationCallStatus.Errored);
    externalIntegrationCallId.current = null;
    props.showMessage(
      `Failed to query external data source, please try again`,
      MessageType.Failure,
    );
  };

  const updateQueryLoadingState = useCallback(
    (status: ExternalIntegrationCallStatus | null) => {
      unwrappedSetQueryLoadingState({ status });
    },
    [unwrappedSetQueryLoadingState],
  );

  const onQuerySubmitted = (call: ExternalIntegrationCall) => {
    updateQueryLoadingState(ExternalIntegrationCallStatus.Processing);
    externalIntegrationCallId.current = call.id;
    props.showMessage('Query submitted to external data source ', MessageType.Success);
  };

  const onQueryCompleted = () => {
    updateQueryLoadingState(ExternalIntegrationCallStatus.Completed);
    externalIntegrationCallId.current = null;
  };

  const shouldContinue = (currentExternalIntegrationCallId: string) =>
    externalIntegrationCallId.current === currentExternalIntegrationCallId;

  /*
    This is a temporary solution to load the external integration call result.
    The work done on this ticket,
    [GRA-2886](https://linear.app/withpersona/issue/GRA-2886/analyze-queries-for-use-in-external-integration-calls),
    will allow us to get the custom source schema inferred from the query.
  */
  const { loading: isLoadingImporter } = useApiLoader({
    loader: async () => {
      if (!params.importerId.startsWith('mprtp')) {
        return;
      }
      const [importer, externalIntegrations] = await Promise.all([
        props.loadImporter(params.importerId),
        props.loadExternalIntegrations(),
      ]);

      const isDataSource = (e: ExternalIntegration): e is ExternalIntegrationDataSource => {
        return ApiModelExternalIntegrationDataSource.includes(e.type);
      };
      const dataSources = externalIntegrations.filter(isDataSource);

      const currentlyAttachedDataSource: ExternalIntegrationDataSource | undefined =
        dataSources.find((e) => e.id === importer.relationships['external-integration'].data.id);
      const defaultTable =
        importer.attributes['pipeline-config']['table-name'] ||
        currentlyAttachedDataSource?.attributes.schema?.tables[0]?.name;
      const defaultBaseQuery = defaultTable ? `SELECT * FROM ${defaultTable}` : '';
      const baseQuery = importer?.attributes['pipeline-config']['base-query'] || defaultBaseQuery;

      if (
        !baseQuery ||
        !QUERYABLE_IMPORTERS.includes(importer.type) ||
        !currentlyAttachedDataSource
      ) {
        return;
      }
      props.runQueryAndPoll({
        externalIntegrationId: importer.relationships['external-integration'].data.id,
        limit: 10,
        onQueryCompleted,
        onQuerySubmitted: (call: ExternalIntegrationCall) => {
          updateQueryLoadingState(ExternalIntegrationCallStatus.Processing);
          externalIntegrationCallId.current = call.id;
        },
        onError,
        onNetworkError,
        onQueryError,
        shouldContinue,
        sql: baseQuery,
      });
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load importer, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [
      props.loadExternalIntegrations,
      props.loadImporter,
      props.showMessage,
      params.importerId,
    ],
  });

  const { loading: isLoadingImporterRuns } = useApiLoader({
    loader: async () => {
      if (params.importerId.startsWith('mprtp')) {
        props.loadImporterRunsList(currentPage, pageSize, params.importerId);
      }
    },
    deps: [props.loadImporterRunsList, currentPage, pageSize, params.importerId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load importer runs.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useEffect(() => {
    return () => {
      // during unmount, we want to clear the externalIntegrationCallId to stop polling for the call
      externalIntegrationCallId.current = null;
      // clear the external integration call so we don't show an unrelated EIC when on another Importer
      props.clearExternalIntegrationCall();
    };
  }, []);

  const runQuery = async (externalIntegrationId: string, sql: string) => {
    await props.runQueryAndPoll({
      externalIntegrationId,
      shouldContinue,
      sql,
      limit: 1,
      onError,
      onNetworkError,
      onQueryError,
      onQuerySubmitted,
      onQueryCompleted,
    });
  };

  const { error: cancelImporterRunError, callback: handleCancelImporterRun } = useApiCallback(
    async (importerRunId: string) => {
      await props.cancelImporterRun(importerRunId);
      props.showMessage('Canceled Importer Run.', MessageType.Success);
    },
    [props.cancelImporterRun, props.showMessage],
  );

  useApiErrorHandler({
    error: cancelImporterRunError,
    onError: () => props.showMessage('Failed to cancel importer run.', MessageType.Failure),
    onNetworkError: () => props.showMessage('Failed to cancel importer run.', MessageType.Failure),
  });

  const importer = props.importers.find((ip: Importer) => ip.id === params.importerId);
  const isDataSource = (e: ExternalIntegration): e is ExternalIntegrationDataSource => {
    return ApiModelExternalIntegrationDataSource.includes(e.type);
  };
  const dataSources = props.externalIntegrations.filter(isDataSource);

  // poll if a run is going or if the importer is recurring
  const timeoutInterval = 5000;
  const timer: MutableRefObject<ReturnType<typeof setInterval> | null> = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  useEffect(() => {
    if (isLoadingImporter || isLoadingImporterRuns) return;

    const importer: Importer | undefined = props.importers.find(
      (ip: Importer) => ip.id === params.importerId,
    );
    if (importer == null) return;

    const hasRunInProgress = props.importerRuns.some(
      (run: ImporterRun) => run.attributes.status === ImporterRunStatus.Running,
    );
    const isRecurring =
      importer.attributes['run-type'] === ImporterRunType.Recurring &&
      importer.attributes.status === ImporterStatus.Active;

    if (isRecurring || hasRunInProgress) {
      // start polling if not already
      if (!timer.current) {
        timer.current = setInterval(async () => {
          await props.loadImporterRunsList(currentPage, pageSize, params.importerId);
          await props.loadImporter(params.importerId);
        }, timeoutInterval);
      }
    }
    // stop polling if no longer need to poll
    else if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }

    // clear the timer upon unmount
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [isLoadingImporter, isLoadingImporterRuns, props.importers, props.importerRuns]);

  if (
    isLoadingAccountTypes ||
    isLoadingImporter ||
    isLoadingImporterRuns ||
    isLoadingTransactionTypes ||
    !importer ||
    !dataSources
  ) {
    // We check if `importer` was found, because isLoading does not default to `true` at first render
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else {
    const activateImporter = async (importer: Importer): Promise<void> => {
      try {
        await props.activateImporter(importer);
      } catch (err) {
        props.showMessage(
          'There was an error activating the importer. Please try again.',
          MessageType.Failure,
        );
        return;
      }

      props.showMessage(
        `Successfully activated ${importer.attributes.name} importer.`,
        MessageType.Success,
      );
    };
    const deactivateImporter = async (importer: Importer): Promise<void> => {
      try {
        await props.deactivateImporter(importer);
      } catch (err) {
        props.showMessage(
          'There was an error deactivating the importer. Please try again.',
          MessageType.Failure,
        );
        return;
      }

      props.showMessage(
        `Successfully deactivated ${importer.attributes.name} importer.`,
        MessageType.Success,
      );
    };
    const archiveImporter = async (importer: Importer): Promise<void> => {
      try {
        await props.archiveImporter(importer);
      } catch (err) {
        props.showMessage(
          'There was an error archiving the importer. Please try again.',
          MessageType.Failure,
        );
        return;
      }

      props.showMessage(
        `Successfully archived ${importer.attributes.name} importer.`,
        MessageType.Success,
      );
    };

    const runImporter = async (importer: Importer): Promise<void> => {
      try {
        await props.createImporterRun(importer.id);
      } catch (err) {
        props.showMessage(
          'There was an error running the importer. Please try again.',
          MessageType.Failure,
        );
        return;
      }

      props.showMessage(
        `Successfully started ${importer.attributes.name} importer sync.`,
        MessageType.Success,
      );
    };

    const activateAndRunImporter = async (importer: Importer): Promise<void> => {
      try {
        await props.activateImporter(importer);
      } catch (err) {
        props.showMessage(
          'There was an error activating the importer. Please try again.',
          MessageType.Failure,
        );
        return;
      }

      try {
        await props.createImporterRun(importer.id);
      } catch (err) {
        props.showMessage(
          'There was an error running the importer. Please try again.',
          MessageType.Failure,
        );
        return;
      }

      props.showMessage(
        `Successfully activated ${importer.attributes.name} importer and started a sync.`,
        MessageType.Success,
      );
    };

    const updateImporter = async (values: EditImporterData): Promise<void> => {
      const config: { [key: string]: any } = {
        destination: values.destination,
        destination_type_id: values.destinationTypeId,
        source_dest_column_mapping: values.sourceDestColumnMapping || {},
        sync_mode: values.syncMode,
        array_sync_mode: values.arraySyncMode,
        archive_after_completion:
          values.archiveAfterCompletion === ImporterArchiveAfterCompletion.True,
      };

      if (QUERYABLE_IMPORTERS.includes(importer.type)) {
        config.base_query = values.baseQuery;
      } else {
        config.table_name = values.tableName;
      }

      // we consider the importer to be set to recurring if a schedule is submitted
      const runType =
        values.scheduleInterval && values.scheduleInterval !== MANUAL_SCHEDULE_STATE
          ? ImporterRunType.Recurring
          : ImporterRunType.Manual;
      if (runType === ImporterRunType.Recurring) {
        config.schedule = {
          type: 'interval_seconds',
          interval_seconds: parseInt(values.scheduleInterval as string),
        };
        // cursor stuff
        const cursorOrder = [
          values.updatedAtCursor && {
            field_name: values.updatedAtCursor,
            direction: 'asc',
          },
          values.primaryIdCursor && {
            field_name: values.primaryIdCursor,
            direction: 'asc',
          },
        ].filter(Boolean);
        if (cursorOrder.length > 0) {
          config.cursor_order = {
            components: cursorOrder,
          };
        }
      }

      try {
        await props.updateImporter(
          importer.id,
          importer.type,
          importer.attributes.name,
          config,
          values.dataSourceId,
          runType,
        );
        props.showMessage('Updated importer successfully.', MessageType.Success, false);
      } catch (err) {
        props.showMessage('Please try again.', MessageType.Failure);
      }
    };

    const updateImporterName = async (name: string): Promise<void> => {
      try {
        await props.updateImporterName(importer.id, importer.type, name);
        props.showMessage('Successfully updated Importer Name', MessageType.Success, false);
      } catch (err) {
        props.showMessage('Please try again.', MessageType.Failure);
      }
    };

    const hasTransactionsEnabled = !productFeatureDisabled(
      props.organization,
      ProductFeatureName.TransactionsDashboard,
      props.currentEnvironment.type,
    );

    return (
      <DashboardImporterView
        accountTypes={props.accountTypes}
        activateAndRunImporter={activateAndRunImporter}
        activateImporter={activateImporter}
        archiveImporter={archiveImporter}
        cancelImporterRun={handleCancelImporterRun}
        currentPage={currentPage}
        dataSources={dataSources}
        deactivateImporter={deactivateImporter}
        externalIntegrationCall={props.externalIntegrationCall}
        handlePageLinkClick={handlePageLinkClick}
        handlePageSizeClick={handlePageSizeClick}
        handleQueryCancel={handleQueryCancel}
        hasTransactionsEnabled={hasTransactionsEnabled}
        importer={importer}
        importerRuns={props.importerRuns}
        pageSize={pageSize}
        paginationParams={paginationParams}
        queryLoadingState={queryLoadingState}
        runImporter={runImporter}
        runQuery={runQuery}
        showMessage={props.showMessage}
        txnTypes={transactionTypes}
        updateImporter={updateImporter}
        updateImporterName={updateImporterName}
      />
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    accountTypes: state.accountTypes.accountTypes,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentPage: state.importerRuns.currentPage,
    externalIntegrations: state.externalIntegrations.externalIntegrations,
    externalIntegrationCall: state.externalIntegrationCall.externalIntegrationCall,
    importers: state.importers.importers,
    importerRuns: state.importerRuns.importerRuns,
    organization: state.organization.organization,
    pageSize: state.importerRuns.pageSize,
    paginationParams: state.importerRuns.paginationParams,
  };
};

const mapDispatchToProps = {
  activateImporter,
  archiveImporter,
  cancelImporterRun,
  clearExternalIntegrationCall,
  createImporterRun,
  deactivateImporter,
  loadExternalIntegrations,
  loadAccountTypes,
  loadImporter,
  loadImporterRunsList,
  saveCurrentPage,
  savePageSize,
  runQueryAndPoll,
  showMessage,
  updateImporter,
  updateImporterName,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardImporterRoute);
